import { useRef, useState, type Dispatch, type SetStateAction } from "react";
import type { CitationTarget, Message, SourceTab } from "../../api/types.js";

export type UseSourceDrawerState = {
  drawerOpen: boolean;
  setDrawerOpen: Dispatch<SetStateAction<boolean>>;
  drawerTab: SourceTab;
  setDrawerTab: Dispatch<SetStateAction<SourceTab>>;
  citationTarget: CitationTarget | null;
  sourceMessageId: string | null;
  setSourceMessageId: Dispatch<SetStateAction<string | null>>;
  sourceMessage: Message | null;
  openCitation: (messageId: string, localIndex: number) => void;
  openMessageSources: (messageId: string | null) => void;
  openReferencePool: (messageId: string | null) => void;
  clearCitationTarget: () => void;
  resetSourceSelection: () => void;
};

export function useSourceDrawer({
  messages,
  currentAssistant
}: {
  messages: Message[];
  currentAssistant: Message | null;
}): UseSourceDrawerState {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState<SourceTab>("cited");
  const [citationTarget, setCitationTarget] = useState<CitationTarget | null>(null);
  const [sourceMessageId, setSourceMessageId] = useState<string | null>(null);
  const navigationRequest = useRef(0);
  const sourceMessage = messages.find((message) => message.id === sourceMessageId) || currentAssistant;

  function clearCitationTarget() {
    setCitationTarget(null);
  }

  function openCitation(messageId: string, localIndex: number) {
    const message = messages.find((candidate) => candidate.id === messageId) || (currentAssistant?.id === messageId ? currentAssistant : null);
    const entry = (message?.meta?.citationMap || []).find((candidate: any) => Number(candidate.localIndex) === localIndex);
    navigationRequest.current += 1;
    setSourceMessageId(messageId);
    setCitationTarget({
      messageId,
      localIndex,
      poolId: String(entry?.poolId || ""),
      referenceItemId: entry?.referenceItemId || null,
      navigationRequestId: navigationRequest.current
    });
    setDrawerTab("cited");
    setDrawerOpen(true);
  }

  function openMessageSources(messageId: string | null) {
    setSourceMessageId(messageId);
    clearCitationTarget();
    setDrawerTab("cited");
    setDrawerOpen(true);
  }

  function openReferencePool(messageId: string | null) {
    setSourceMessageId(messageId);
    clearCitationTarget();
    setDrawerTab("pool");
    setDrawerOpen(true);
  }

  function resetSourceSelection() {
    clearCitationTarget();
    setSourceMessageId(null);
  }

  return {
    drawerOpen,
    setDrawerOpen,
    drawerTab,
    setDrawerTab,
    citationTarget,
    sourceMessageId,
    setSourceMessageId,
    sourceMessage,
    openCitation,
    openMessageSources,
    openReferencePool,
    clearCitationTarget,
    resetSourceSelection
  };
}
